"use client";

/**
 * AikoTour - a product-tour engine (spotlight coach-marks) built ON the lib's
 * existing Aiko anchor layer, not on a third-party tour library.
 *
 *   <AikoTour
 *     steps={[
 *       { anchorId: "home.composer", title: "Start here", body: "Describe what you want to build." },
 *       { anchorId: "home.create-tile", title: "Create", body: "Spin up a studio.", side: "bottom" },
 *     ]}
 *     storageKey="home-first-run"
 *     onComplete={() => markOnboarded()}
 *   />
 *
 * Targets are marked the lib's normal way - `<AikoAnchor id="home.composer">` or a
 * raw `data-aiko-anchor="home.composer"`. The tour resolves each step's `anchorId`
 * through `useAikoTarget` (MutationObserver-backed, SSR-safe), pins a Radix Popover
 * tooltip to the live element via the same `virtualRef` bridge `AikoSurfaces` uses,
 * and paints a single-box-shadow spotlight cutout around the target rect.
 *
 * What this reuses (so the spotlight math is not re-derived):
 * - `useAikoTarget` - resolve `anchorId` -> live element + visibility, re-render on DOM churn.
 * - the `useVirtualAnchor` pattern from `AikoSurfaces` - pin a portalled Popover to a moving node.
 * - `Stepper`'s headless state - `next` / `prev` / `goto` / `complete` + a11y (we drive it as a render-prop).
 * - the lib motion presets (`springs` / `transitions` / `durations`) + `useReducedMotion`.
 * - `useLocalStorage` - persist the "seen" flag so a one-time tour does not re-run.
 *
 * Preserved from the fe-react donor (`WalkthroughTooltipSpotlight`): the
 * box-shadow cutout technique (massive spread dims the screen, the target rect
 * stays a transparent hole with a 2px inner ring), border-radius matching with a
 * 12px fallback, padding around the hole, scroll-into-view then settle-then-lock
 * (including the 1s reduced-motion fallback and the "already there" fast path),
 * directional dashed SVG arrow, progress dots, and Back/Next/Done + step-0 dismiss.
 *
 * Decoupled from the donor's app-knowledge: no `#0A2540` / `text-gray-*` (brand
 * tokens only), no `explore-studios` / `sidebar_expanded_state` special-cases
 * (replaced by a per-step `padding` prop), no `walkthrough-highlight` global
 * class (replaced by a `data-aiko-tour-active` attribute the consumer may style),
 * no raw `querySelector` (anchor ids), no baked-in icon map (consumer passes nodes).
 *
 * Light theme only. Every animation is gated on `useReducedMotion()` with a static
 * fallback; reduced motion also switches scroll to `behavior:"auto"` (which exercises
 * the donor's 1s lock fallback path).
 */
import * as React from "react";
import { createPortal } from "react-dom";
import * as Popover from "@radix-ui/react-popover";
import { motion, AnimatePresence } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "../lib/cn";
import { springs, transitions, durations, easings } from "../lib/motion";
import { useAikoTarget } from "../hooks/useAikoTarget";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { useMounted } from "../hooks/useMounted";
import { useLocalStorage } from "../hooks/useLocalStorage";

/* ============================================================================
 * Types
 * ========================================================================= */

type Side = "top" | "right" | "bottom" | "left";
type Align = "start" | "center" | "end";

/** Symmetric or per-edge padding (px) added around the spotlight hole. */
export type TourPadding = number | { top?: number; right?: number; bottom?: number; left?: number };

/** One bullet line in a step body. */
export interface TourBullet {
  /** Optional rendered icon node (consumer passes it; the lib owns no icon registry). */
  icon?: React.ReactNode;
  text: React.ReactNode;
}

/** One mini-card in a step's 2-column grid body. */
export interface TourGridItem {
  icon?: React.ReactNode;
  heading: React.ReactNode;
  body: React.ReactNode;
}

/**
 * One tour step. `anchorId` is resolved via `useAikoTarget` against a
 * `<AikoAnchor id>` / `data-aiko-anchor` in the DOM. The body can be a plain
 * `body` node, a `bullets` list, or a `grid` of mini-cards (matching the
 * donor's three content shapes); the first one set wins in that order.
 */
export interface TourStep {
  /** Anchor id matching `<AikoAnchor id>` (or a raw `data-aiko-anchor`). */
  anchorId: string;
  /** Step heading. */
  title: React.ReactNode;
  /** Plain body copy. */
  body?: React.ReactNode;
  /** Bullet-list body (icon + text per line). */
  bullets?: TourBullet[];
  /** 2-column grid body (mini-cards). Presence defaults the card to the `wide` size. */
  grid?: TourGridItem[];
  /** Side of the anchor to place the tooltip. Default `"bottom"`. */
  side?: Side;
  /** Alignment along the chosen side. Default `"center"`. */
  align?: Align;
  /** Padding around the spotlight hole. Default 8px every side. */
  padding?: TourPadding;
  /** Px scroll offset from the top when scrolling this step into view. Falls back to the tour-level `scrollOffset`. */
  scrollOffset?: number;
  /**
   * What to do when this step's anchor is missing or hidden:
   *   - `"queue"` (default) - wait for it (bounded by `queueTimeoutMs`), then auto-skip the step.
   *   - `"skip"`            - advance to the next step immediately.
   */
  onUnreachable?: "queue" | "skip";
}

export interface AikoTourProps {
  /** Ordered steps. Order is the navigation order. */
  steps: TourStep[];

  /** Controlled open state. Pair with `onOpenChange`. */
  open?: boolean;
  /** Uncontrolled initial open state. Default `true` (a first-run tour opens on mount). */
  defaultOpen?: boolean;
  /** Fires whenever the tour opens or closes (skip / complete both close it). */
  onOpenChange?: (open: boolean) => void;

  /** Controlled active step index. Pair with `onIndexChange`. */
  index?: number;
  /** Uncontrolled initial step index. Default `0`. */
  defaultIndex?: number;
  /** Fires when the active step changes. */
  onIndexChange?: (index: number) => void;

  /** Fires when the user finishes the last step (Done / next past the end). Also marks the tour seen. */
  onComplete?: () => void;
  /** Fires when the user dismisses the tour early (step-0 X / Esc). Also marks the tour seen. */
  onSkip?: () => void;

  /**
   * localStorage key for the one-time "seen" flag. When set, the tour will not
   * auto-open if it has already been completed or skipped (uncontrolled `open`
   * only). Omit to make the tour run every time (consumer owns persistence).
   */
  storageKey?: string;

  /** Default px top offset when scrolling a target into view. Default 120 (header clearance). */
  scrollOffset?: number;
  /** Backdrop dim opacity for the spotlight (0..1). Default 0.75. */
  overlayOpacity?: number;
  /**
   * Show the dismiss X only on step 0 (donor behavior). Default `true`. When
   * `false`, the X shows on every step.
   */
  dismissOnStepZeroOnly?: boolean;
  /** Bound (ms) on how long a `"queue"` step waits for its anchor before auto-skipping. Default 30000. */
  queueTimeoutMs?: number;

  /** Copy override for the advance button on non-final steps. Default `"Next"`. */
  nextLabel?: string;
  /** Copy override for the advance button on the final step. Default `"Done"`. */
  doneLabel?: string;
  /** Copy override for the back button. Default `"Back"`. */
  backLabel?: string;
}

/* ============================================================================
 * useAikoTour - small controller for open + seen state
 * ========================================================================= */

export interface UseAikoTourOptions {
  /** localStorage key for the one-time "seen" flag. */
  storageKey?: string;
  /** Auto-open on mount when the tour has not been seen. Default `true`. */
  autoStart?: boolean;
}

export interface AikoTourController {
  /** Whether the tour should currently be open. */
  open: boolean;
  /** Whether the tour has already been completed/skipped (per `storageKey`). */
  seen: boolean;
  /** Open the tour. */
  start: () => void;
  /** Close the tour without marking it seen. */
  stop: () => void;
  /** Close the tour and mark it seen (so it will not auto-start again). */
  finish: () => void;
  /** Clear the seen flag (so it can auto-start again - useful for "replay tour"). */
  reset: () => void;
  /** Props to spread onto `<AikoTour {...controller.tourProps}>`. */
  tourProps: Pick<AikoTourProps, "open" | "onComplete" | "onSkip"> & {
    onOpenChange: (open: boolean) => void;
  };
}

/**
 * Drive an `AikoTour`'s open + one-time-seen lifecycle. Persists the seen flag
 * via `useLocalStorage` so a first-run tour does not re-run after completion or
 * skip. SSR-safe (storage hydrates on the first client effect).
 *
 *   const tour = useAikoTour({ storageKey: "home-first-run" });
 *   return <AikoTour steps={steps} {...tour.tourProps} />;
 *   // …and a "Replay tour" button can call tour.reset() then tour.start().
 */
export function useAikoTour(options: UseAikoTourOptions = {}): AikoTourController {
  const { storageKey, autoStart = true } = options;
  // A stable fallback key keeps the hook order stable when no storageKey is
  // given; with no key we never persist (treat as "never seen").
  const [seen, setSeen, clearSeen] = useLocalStorage<boolean>(
    storageKey ?? "aisquare-ui-aiko-tour-ephemeral",
    false,
  );
  const persisted = storageKey != null;

  const [open, setOpen] = React.useState(false);
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    if (startedRef.current) return;
    if (!autoStart) return;
    if (persisted && seen) return;
    startedRef.current = true;
    setOpen(true);
  }, [autoStart, persisted, seen]);

  const start = React.useCallback(() => setOpen(true), []);
  const stop = React.useCallback(() => setOpen(false), []);
  const finish = React.useCallback(() => {
    setOpen(false);
    if (persisted) setSeen(true);
  }, [persisted, setSeen]);
  const reset = React.useCallback(() => {
    if (persisted) clearSeen();
    startedRef.current = false;
  }, [persisted, clearSeen]);

  const onOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next && persisted) setSeen(true);
    },
    [persisted, setSeen],
  );

  return {
    open,
    seen: persisted ? seen : false,
    start,
    stop,
    finish,
    reset,
    tourProps: { open, onOpenChange, onComplete: finish, onSkip: finish },
  };
}

/* ============================================================================
 * Scroll-into-view then settle-then-lock - preserved from the donor.
 * ========================================================================= */

/** Walk ancestors for the first vertically-scrollable parent (else `window`). */
function getScrollParent(node: HTMLElement | null): HTMLElement | Window {
  if (!node || typeof window === "undefined") return typeof window === "undefined" ? (null as never) : window;
  if (node === document.body || node === document.documentElement) return window;
  const style = window.getComputedStyle(node);
  const overflowY = style.overflowY;
  const scrollable = overflowY !== "visible" && overflowY !== "hidden";
  if (scrollable && node.scrollHeight > node.clientHeight) return node;
  return getScrollParent(node.parentElement);
}

/**
 * Scroll `target` into view (top `offset` clearance), then lock scroll once the
 * scroll settles. Critical edge cases preserved from the donor:
 *   - a 100ms-debounced "scroll ended" detector locks after smooth scroll settles,
 *   - a 1s fallback `setTimeout(lock)` so reduced-motion users (whose `auto`
 *     scroll fires no scroll events) still get locked,
 *   - an "already there" fast path (`|delta| < 10`) locks immediately.
 * Cleanup restores the original overflow on body + container.
 */
function useScrollLockToTarget(
  target: HTMLElement | null,
  opts: { enabled: boolean; offset: number; reduced: boolean | null },
) {
  const { enabled, offset, reduced } = opts;
  React.useEffect(() => {
    if (!enabled || !target || typeof document === "undefined") return;

    const scrollParent = getScrollParent(target);
    const containerEl = scrollParent instanceof HTMLElement ? scrollParent : null;
    const originalBodyOverflow = document.body.style.overflow;
    const originalContainerOverflow = containerEl ? containerEl.style.overflow : "";
    const behavior: ScrollBehavior = reduced ? "auto" : "smooth";

    let locked = false;
    let scrollTimeout: ReturnType<typeof setTimeout> | undefined;
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
    let onScroll: (() => void) | null = null;

    const lock = () => {
      if (locked) return;
      document.body.style.overflow = "hidden";
      if (containerEl) containerEl.style.overflow = "hidden";
      locked = true;
      if (onScroll) {
        (scrollParent as Window | HTMLElement).removeEventListener("scroll", onScroll);
        onScroll = null;
      }
    };

    onScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      // No scroll events for 100ms -> assume the scroll has settled.
      scrollTimeout = setTimeout(lock, 100);
    };

    const rect = target.getBoundingClientRect();

    if (containerEl) {
      const containerRect = containerEl.getBoundingClientRect();
      const destination = containerEl.scrollTop + (rect.top - containerRect.top) - offset;
      if (Math.abs(containerEl.scrollTop - destination) < 10) {
        lock();
      } else {
        containerEl.addEventListener("scroll", onScroll, { passive: true });
        containerEl.scrollTo({ top: destination, behavior });
        // Fallback in case no scroll event fires (reduced motion / instant jump).
        fallbackTimer = setTimeout(lock, 1000);
      }
    } else {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const destination = rect.top + scrollTop - offset;
      if (Math.abs(scrollTop - destination) < 10) {
        lock();
      } else {
        window.addEventListener("scroll", onScroll, { passive: true });
        window.scrollTo({ top: destination, behavior });
        fallbackTimer = setTimeout(lock, 1000);
      }
    }

    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (onScroll) {
        (scrollParent as Window | HTMLElement).removeEventListener("scroll", onScroll);
      }
      document.body.style.overflow = originalBodyOverflow;
      if (containerEl) containerEl.style.overflow = originalContainerOverflow;
    };
    // Re-run when the resolved target changes (next step) or enable flips.
  }, [enabled, target, offset, reduced]);
}

/* ============================================================================
 * Spotlight cutout - fullscreen dimmer with a transparent hole.
 * ========================================================================= */

interface CutoutRect {
  top: number;
  left: number;
  width: number;
  height: number;
  borderRadius: number;
}

function resolvePadding(padding: TourPadding | undefined): {
  top: number;
  right: number;
  bottom: number;
  left: number;
} {
  if (padding == null) return { top: 8, right: 8, bottom: 8, left: 8 };
  if (typeof padding === "number") {
    return { top: padding, right: padding, bottom: padding, left: padding };
  }
  return {
    top: padding.top ?? 0,
    right: padding.right ?? 0,
    bottom: padding.bottom ?? 0,
    left: padding.left ?? 0,
  };
}

/** Parse the target's computed `borderRadius` to a px number, 12px fallback when sharp. */
function readBorderRadius(el: HTMLElement): number {
  if (typeof window === "undefined") return 12;
  const raw = window.getComputedStyle(el).borderTopLeftRadius;
  const parsed = parseFloat(raw);
  if (!parsed || Number.isNaN(parsed)) return 12;
  return parsed;
}

/**
 * The dimming overlay + transparent hole. A single `fixed` div sized to the
 * padded target rect paints the donor's box-shadow trick: a huge spread dims
 * the whole screen while the rect itself stays clear, with a soft inner ring.
 * `pointer-events:none` keeps the highlighted element interactive. The rect
 * spring-glides between steps (instant under reduced motion).
 */
function Spotlight({
  rect,
  overlayOpacity,
  reduced,
}: {
  rect: CutoutRect | null;
  overlayOpacity: number;
  reduced: boolean | null;
}) {
  const mounted = useMounted();
  if (!mounted || typeof document === "undefined") return null;
  if (!rect) return null;

  const allowMotion = reduced === false || reduced === null;
  const dim = `0 0 0 9999px rgba(11, 20, 40, ${overlayOpacity})`;
  // Inner ring uses the brand navy at high opacity so the hole edge reads as a
  // crisp focus frame (donor used white; navy keeps it on-brand for the light theme).
  const ring = "inset 0 0 0 2px rgba(255, 255, 255, 0.92)";

  return createPortal(
    <motion.div
      aria-hidden
      className="fixed z-[9998] pointer-events-none"
      initial={false}
      animate={{
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        borderRadius: rect.borderRadius,
        boxShadow: `${dim}, ${ring}`,
      }}
      transition={allowMotion ? springs.gentle : { duration: 0 }}
      style={{ position: "fixed" }}
    />,
    document.body,
  );
}

/* ============================================================================
 * Directional arrow - curved dashed connector pointing back at the target.
 * ========================================================================= */

const ARROW_SIZE = 60;
const ARROW_GAP = 8;

/** Per-side geometry: a curved dashed line plus a two-stroke V arrowhead. */
function arrowGeometry(side: Side): {
  style: React.CSSProperties;
  line: string;
  head1: string;
  head2: string;
} {
  const base: React.CSSProperties = {
    position: "absolute",
    width: ARROW_SIZE,
    height: ARROW_SIZE,
    pointerEvents: "none",
  };
  switch (side) {
    // `side` is where the tooltip sits relative to the target, so the arrow
    // points from the tooltip back toward the target (opposite edge of the card).
    case "bottom":
      return {
        style: { ...base, top: -ARROW_SIZE - ARROW_GAP, left: "50%", transform: "translateX(-50%)" },
        line: "M 25 48 C 20 30 30 15 25 8",
        head1: "M 25 8 L 20 16",
        head2: "M 25 8 L 30 16",
      };
    case "top":
      return {
        style: { ...base, bottom: -ARROW_SIZE - ARROW_GAP, left: "50%", transform: "translateX(-50%)" },
        line: "M 25 2 C 30 20 20 35 25 42",
        head1: "M 25 42 L 20 34",
        head2: "M 25 42 L 30 34",
      };
    case "left":
      return {
        style: { ...base, right: -ARROW_SIZE - ARROW_GAP, top: "50%", transform: "translateY(-50%)" },
        line: "M 2 25 C 20 20 35 30 42 25",
        head1: "M 42 25 L 34 20",
        head2: "M 42 25 L 34 30",
      };
    case "right":
    default:
      return {
        style: { ...base, left: -ARROW_SIZE - ARROW_GAP, top: "50%", transform: "translateY(-50%)" },
        line: "M 48 25 C 30 30 15 20 8 25",
        head1: "M 8 25 L 16 20",
        head2: "M 8 25 L 16 30",
      };
  }
}

function TourArrow({ side, reduced }: { side: Side; reduced: boolean | null }) {
  const geo = arrowGeometry(side);
  const allowMotion = reduced === false || reduced === null;
  // Brand navy connector (donor used white over a dark overlay; the tooltip is
  // a light card, so the navy stroke reads against both the card and the dim).
  const stroke = "currentColor";
  const drawIn = allowMotion
    ? { initial: { pathLength: 0, opacity: 0 }, animate: { pathLength: 1, opacity: 1 } }
    : { initial: false as const, animate: { pathLength: 1, opacity: 1 } };
  return (
    <div style={geo.style} className="text-primary">
      <svg width="100%" height="100%" viewBox="0 0 50 50" overflow="visible" aria-hidden>
        <motion.path
          d={geo.line}
          fill="none"
          stroke={stroke}
          strokeWidth={2}
          strokeLinecap="round"
          strokeDasharray="4 3"
          initial={drawIn.initial}
          animate={drawIn.animate}
          transition={{ duration: durations.surface, ease: easings.smoothOut }}
        />
        <path d={geo.head1} fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" />
        <path d={geo.head2} fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" />
      </svg>
    </div>
  );
}

/* ============================================================================
 * AikoSpotlight - the single-step renderer (cutout + tooltip card).
 * ========================================================================= */

const tourCardVariants = cva(
  [
    "relative rounded-xl border border-border bg-popover text-popover-foreground",
    "shadow-[0_16px_40px_-16px_rgb(11_20_40/0.45)]",
    "focus:outline-none",
  ].join(" "),
  {
    variants: {
      size: {
        compact: "w-[280px] max-w-[calc(100vw-32px)]",
        wide: "w-[440px] max-w-[calc(100vw-32px)]",
      },
    },
    defaultVariants: { size: "compact" },
  },
);

export interface AikoSpotlightProps extends VariantProps<typeof tourCardVariants> {
  /** The resolved target element to spotlight + anchor against. */
  target: HTMLElement | null;
  /** Step content. */
  step: TourStep;
  /** Zero-based index of this step. */
  index: number;
  /** Total step count (for the progress dots + Done label). */
  total: number;
  /** Backdrop dim opacity. */
  overlayOpacity: number;
  /** Top offset (px) when scrolling the target into view. */
  scrollOffset: number;
  /** Show the dismiss X only on step 0. */
  dismissOnStepZeroOnly: boolean;
  /** Button copy. */
  nextLabel: string;
  doneLabel: string;
  backLabel: string;
  /** Navigation callbacks (wired to the Stepper state by `AikoTour`). */
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onGoto: (index: number) => void;
}

/**
 * Renders one tour step: the spotlight cutout around `target` plus a Radix
 * Popover tooltip card pinned to it via the `virtualRef` bridge. Named
 * `AikoSpotlight` (never bare `Spotlight`) to avoid confusion with `SpotlightCard`.
 *
 * Exported standalone so a consumer can drive a single coach-mark without the
 * full multi-step orchestrator (pass a resolved element + a one-element config).
 */
export const AikoSpotlight = React.forwardRef<HTMLDivElement, AikoSpotlightProps>(
  (
    {
      target,
      step,
      index,
      total,
      overlayOpacity,
      scrollOffset,
      dismissOnStepZeroOnly,
      nextLabel,
      doneLabel,
      backLabel,
      onNext,
      onPrev,
      onSkip,
      onGoto,
      size,
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const allowMotion = reduced === false || reduced === null;
    const side: Side = step.side ?? "bottom";
    const align: Align = step.align ?? "center";
    const isLast = index >= total - 1;
    const showDismiss = dismissOnStepZeroOnly ? index === 0 : true;
    const resolvedSize = size ?? (step.grid ? "wide" : "compact");

    // Scroll the target into view then lock scroll once it settles.
    useScrollLockToTarget(target, {
      enabled: !!target,
      offset: step.scrollOffset ?? scrollOffset,
      reduced,
    });

    // A `data-aiko-tour-active` marker the consumer can style if they want extra
    // emphasis. Replaces the donor's global `walkthrough-highlight` class.
    React.useEffect(() => {
      if (!target) return;
      target.setAttribute("data-aiko-tour-active", "");
      return () => target.removeAttribute("data-aiko-tour-active");
    }, [target]);

    // Re-measure the target on scroll / resize so the cutout tracks layout
    // shifts. Radix Popper already tracks the tooltip via the virtualRef; the
    // cutout is our own fixed div, so it needs its own measure loop. We use
    // a throttled rAF tick rather than a per-frame loop - cheaper, and the
    // spring on the rect smooths any visible stepping.
    const [rect, setRect] = React.useState<CutoutRect | null>(null);
    React.useEffect(() => {
      if (!target) {
        setRect(null);
        return;
      }
      const pad = resolvePadding(step.padding);
      let frame = 0;
      let pending = false;
      const measure = () => {
        pending = false;
        const r = target.getBoundingClientRect();
        setRect({
          top: r.top - pad.top,
          left: r.left - pad.left,
          width: r.width + pad.left + pad.right,
          height: r.height + pad.top + pad.bottom,
          borderRadius: readBorderRadius(target),
        });
      };
      const schedule = () => {
        if (pending) return;
        pending = true;
        frame = requestAnimationFrame(measure);
      };
      measure();
      window.addEventListener("scroll", schedule, { passive: true, capture: true });
      window.addEventListener("resize", schedule, { passive: true });
      // Track DOM/layout churn around the target (e.g. async content settling).
      const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
      ro?.observe(target);
      return () => {
        cancelAnimationFrame(frame);
        window.removeEventListener("scroll", schedule, { capture: true } as EventListenerOptions);
        window.removeEventListener("resize", schedule);
        ro?.disconnect();
      };
    }, [target, step.padding]);

    // Bridge the live element into Radix Popover's virtualRef (same pattern as
    // AikoSurfaces' useVirtualAnchor - measured at call time so it tracks moves).
    const virtualRef = React.useMemo<
      React.RefObject<{ getBoundingClientRect: () => DOMRect }>
    >(() => {
      const measurable = {
        getBoundingClientRect: () =>
          target?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0),
      };
      return { current: measurable };
    }, [target]);

    const handleOpenChange = (next: boolean) => {
      // Esc / outside interaction closes -> treat as skip (donor: only step 0
      // has an explicit X, but Esc should always let the user out).
      if (!next) onSkip();
    };

    const cardMotion = allowMotion
      ? {
          initial: { opacity: 0, y: 8 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: 8 },
          transition: transitions.surface,
        }
      : {
          initial: false as const,
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0 },
        };

    return (
      <>
        <Spotlight rect={rect} overlayOpacity={overlayOpacity} reduced={reduced} />

        <Popover.Root open onOpenChange={handleOpenChange}>
          <Popover.Anchor virtualRef={virtualRef} />
          <Popover.Portal>
            <Popover.Content
              ref={ref}
              side={side}
              align={align}
              sideOffset={ARROW_SIZE}
              collisionPadding={24}
              role="dialog"
              aria-modal="false"
              aria-label="Product tour"
              onOpenAutoFocus={(e) => e.preventDefault()}
              onCloseAutoFocus={(e) => e.preventDefault()}
              className="z-[9999] focus:outline-none"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={index}
                  className={cn(tourCardVariants({ size: resolvedSize }))}
                  initial={cardMotion.initial}
                  animate={cardMotion.animate}
                  exit={allowMotion ? { opacity: 0, y: 8 } : { opacity: 1, y: 0 }}
                  transition={cardMotion.transition}
                >
                  <TourArrow side={side} reduced={reduced} />

                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 px-4 pt-3.5 pb-1.5">
                    <h3 className="text-sm font-semibold leading-snug text-foreground">
                      {step.title}
                    </h3>
                    {showDismiss ? (
                      <button
                        type="button"
                        onClick={onSkip}
                        aria-label="Skip tour"
                        className={cn(
                          "-mr-1 -mt-0.5 shrink-0 rounded p-0.5",
                          "text-muted-foreground hover:bg-muted hover:text-foreground",
                          "transition-colors duration-150",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        )}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    ) : null}
                  </div>

                  {/* Body - grid wins, then bullets, then plain body. */}
                  <div className="px-4 pb-3 text-xs leading-relaxed text-muted-foreground">
                    {step.grid ? (
                      <div className="mt-1 grid grid-cols-2 gap-2">
                        {step.grid.map((item, i) => (
                          <div
                            key={i}
                            className="rounded-md border border-border bg-background p-2"
                          >
                            <div className="mb-0.5 flex items-center gap-1.5">
                              {item.icon ? (
                                <span className="shrink-0 text-primary [&_svg]:h-3 [&_svg]:w-3">
                                  {item.icon}
                                </span>
                              ) : null}
                              <span className="text-[11px] font-medium text-foreground">
                                {item.heading}
                              </span>
                            </div>
                            <p className="text-[10px] leading-tight text-muted-foreground">
                              {item.body}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : step.bullets ? (
                      <ul className="mt-1 space-y-1.5">
                        {step.bullets.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            {item.icon ? (
                              <span className="mt-0.5 shrink-0 text-primary [&_svg]:h-3.5 [&_svg]:w-3.5">
                                {item.icon}
                              </span>
                            ) : null}
                            <span>{item.text}</span>
                          </li>
                        ))}
                      </ul>
                    ) : step.body ? (
                      <p>{step.body}</p>
                    ) : null}
                  </div>

                  {/* Footer - progress dots + Back / Next-or-Done. */}
                  <div
                    className={cn(
                      "flex items-center justify-between gap-3 rounded-b-xl border-t border-border px-4 py-2.5",
                      "bg-background/60",
                    )}
                  >
                    <div className="flex items-center gap-1" role="tablist" aria-label={`Step ${index + 1} of ${total}`}>
                      {Array.from({ length: total }).map((_, i) => {
                        const current = i === index;
                        return (
                          <button
                            key={i}
                            type="button"
                            role="tab"
                            aria-selected={current}
                            aria-label={`Go to step ${i + 1}`}
                            tabIndex={current ? 0 : -1}
                            onClick={() => onGoto(i)}
                            className={cn(
                              "h-1.5 rounded-full transition-all duration-200",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                              current ? "w-4 bg-primary" : "w-1.5 bg-border hover:bg-muted-foreground/40",
                            )}
                          />
                        );
                      })}
                    </div>

                    <div className="flex items-center gap-1.5">
                      {index > 0 ? (
                        <button
                          type="button"
                          onClick={onPrev}
                          className={cn(
                            "rounded px-2 py-1 text-[11px] font-medium",
                            "text-muted-foreground hover:text-foreground",
                            "transition-colors duration-150",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          )}
                        >
                          {backLabel}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={onNext}
                        className={cn(
                          "inline-flex h-7 items-center justify-center rounded-md px-3",
                          "bg-primary text-primary-foreground hover:bg-primary/90",
                          "text-[11px] font-semibold shadow-sm",
                          "transition-colors duration-150",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                        )}
                      >
                        {isLast ? doneLabel : nextLabel}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </>
    );
  },
);
AikoSpotlight.displayName = "AikoSpotlight";

/* ============================================================================
 * AikoTour - the orchestrator.
 * ========================================================================= */

/**
 * Multi-step product tour. Drives an ordered `steps` config: resolves each
 * step's `anchorId` via `useAikoTarget`, renders an `AikoSpotlight` against the
 * live element, and sequences next / prev / skip / complete. Step state mirrors
 * `Stepper`'s headless contract (controlled via `index` + `onIndexChange`, or
 * uncontrolled via `defaultIndex`). When a step's anchor is unreachable, the
 * `onUnreachable` policy ("queue" with a bounded wait, or "skip") decides whether
 * to wait for it or advance - reusing the `ActiveHint` policy shape.
 *
 * Pair with `useAikoTour` for the one-time-seen lifecycle, or drive `open` +
 * `onComplete` / `onSkip` yourself. Returns `null` (renders nothing) while closed
 * or while there are no steps.
 */
export function AikoTour({
  steps,
  open: openProp,
  defaultOpen = true,
  onOpenChange,
  index: indexProp,
  defaultIndex = 0,
  onIndexChange,
  onComplete,
  onSkip,
  storageKey,
  scrollOffset = 120,
  overlayOpacity = 0.75,
  dismissOnStepZeroOnly = true,
  queueTimeoutMs = 30_000,
  nextLabel = "Next",
  doneLabel = "Done",
  backLabel = "Back",
}: AikoTourProps) {
  const total = steps.length;

  // One-time "seen" persistence (uncontrolled `open` only). When seen, the tour
  // does not auto-open; explicit `open` always wins.
  const [seen, setSeen] = useLocalStorage<boolean>(
    storageKey ?? "aisquare-ui-aiko-tour-seen-ephemeral",
    false,
  );
  const persisted = storageKey != null;

  const openControlled = openProp !== undefined;
  const [openInternal, setOpenInternal] = React.useState<boolean>(() =>
    persisted ? defaultOpen && !seen : defaultOpen,
  );
  const open = openControlled ? (openProp as boolean) : openInternal;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!openControlled) setOpenInternal(next);
      onOpenChange?.(next);
    },
    [openControlled, onOpenChange],
  );

  const indexControlled = indexProp !== undefined;
  const [indexInternal, setIndexInternal] = React.useState(() =>
    clamp(defaultIndex, 0, Math.max(0, total - 1)),
  );
  const index = clamp(
    indexControlled ? (indexProp as number) : indexInternal,
    0,
    Math.max(0, total - 1),
  );

  const setIndex = React.useCallback(
    (next: number) => {
      const clamped = clamp(next, 0, total - 1);
      if (!indexControlled) setIndexInternal(clamped);
      onIndexChange?.(clamped);
    },
    [indexControlled, onIndexChange, total],
  );

  const markSeen = React.useCallback(() => {
    if (persisted) setSeen(true);
  }, [persisted, setSeen]);

  const complete = React.useCallback(() => {
    setOpen(false);
    markSeen();
    onComplete?.();
  }, [setOpen, markSeen, onComplete]);

  const skip = React.useCallback(() => {
    setOpen(false);
    markSeen();
    onSkip?.();
  }, [setOpen, markSeen, onSkip]);

  const next = React.useCallback(() => {
    if (index >= total - 1) {
      complete();
      return;
    }
    setIndex(index + 1);
  }, [index, total, complete, setIndex]);

  const prev = React.useCallback(() => {
    if (index <= 0) return;
    setIndex(index - 1);
  }, [index, setIndex]);

  const goto = React.useCallback((i: number) => setIndex(i), [setIndex]);

  const currentStep = steps[index];

  // Resolve the current step's anchor. Hooks must run unconditionally, so this
  // is always called even when closed (with a null id -> empty snapshot).
  const targetState = useAikoTarget(open && currentStep ? currentStep.anchorId : null);
  const reachable = targetState.exists && targetState.visible;

  // onUnreachable policy. A `"skip"` step advances immediately when its anchor
  // is missing; a `"queue"` step waits (bounded by `queueTimeoutMs`) and then
  // auto-advances so a broken anchor never wedges the tour. Disabled-but-visible
  // targets are reachable (we still spotlight them).
  const policy = currentStep?.onUnreachable ?? "queue";
  React.useEffect(() => {
    if (!open || !currentStep) return;
    if (reachable) return;
    const advance = () => {
      // Skip the step; if it was the last one, finish the tour.
      if (index >= total - 1) complete();
      else setIndex(index + 1);
    };
    if (policy === "skip") {
      const id = window.setTimeout(advance, 0);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(advance, queueTimeoutMs);
    return () => window.clearTimeout(id);
  }, [open, currentStep, reachable, policy, queueTimeoutMs, index, total, complete, setIndex]);

  // Escape-to-skip at the orchestrator level too (Popover handles it while the
  // card is focused, but a global listener covers the gap before first focus).
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") skip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, skip]);

  if (!open || total === 0 || !currentStep) return null;
  // While a queue step's anchor is still unreachable, render nothing (no broken
  // spotlight) - the effect above is waiting for it or counting down to skip.
  if (!reachable) return null;

  return (
    <AikoSpotlight
      target={targetState.element}
      step={currentStep}
      index={index}
      total={total}
      overlayOpacity={overlayOpacity}
      scrollOffset={scrollOffset}
      dismissOnStepZeroOnly={dismissOnStepZeroOnly}
      nextLabel={nextLabel}
      doneLabel={doneLabel}
      backLabel={backLabel}
      onNext={next}
      onPrev={prev}
      onSkip={skip}
      onGoto={goto}
    />
  );
}
AikoTour.displayName = "AikoTour";

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  if (n < min) return min;
  if (n > max) return max;
  return n;
}
